import {
  getFieldValue,
  calculateCellValue,
  getCellText,
  getGroupKeyText,
  isDateType,
  toDate,
} from "./format";

function sanitizeSheetName(name) {
  const clean = String(name || "Sheet")
    .replace(/[[\]:*?/\\]/g, " ")
    .trim();

  return (clean || "Sheet").slice(0, 31);
}

function excelDateFormat(format, dataType) {
  const pattern =
    typeof format === "string" && format
      ? format
      : dataType === "datetime"
      ? "dd/MM/yyyy HH:mm"
      : "dd/MM/yyyy";

  return pattern
    .replace(/EEEE/g, "dddd")
    .replace(/EE+/g, "ddd")
    .replace(/MM/g, "mm")
    .replace(/HH/g, "hh")
    .replace(/a/g, "AM/PM");
}

function localAsUtc(date) {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    )
  );
}

function plainNumberColumn(column) {
  return (
    column.dataType === "number" &&
    !column.customizeText &&
    !column.calculateDisplayValue &&
    !(column.format && typeof column.format === "object" && column.format.formatter)
  );
}

function booleanText(column, value, t) {
  if (value == null) return "";
  if (value === true) return column.trueText ?? t("yes");
  if (value === false) return column.falseText ?? t("no");

  return String(value);
}

function cellExport(column, data, locale, t) {
  const value = calculateCellValue(column, data);

  if (value == null) return { text: "", value: null };

  if (plainNumberColumn(column) && typeof value === "number")
    return {
      value,
      numFmt: typeof column.format === "string" ? column.format.replace(/#+/g, "0") : "0.00",
      text: getCellText(column, data, value, locale, "export"),
    };

  if (isDateType(column.dataType) && !column.customizeText && !column.calculateDisplayValue) {
    const date = toDate(value);

    if (date)
      return {
        value: localAsUtc(date),
        numFmt: excelDateFormat(column.format, column.dataType),
        text: getCellText(column, data, date, locale, "export"),
      };
  }

  if (column.dataType === "boolean" && !column.customizeText && !column.calculateDisplayValue) {
    const text = booleanText(column, value, t);

    return { value: text, text };
  }

  const text = getCellText(column, data, value, locale, "export");

  return { value: text, text };
}

function buildRows(data, columns, groups, locale, t) {
  const rows = [];
  let previousPath = [];

  data.forEach(item => {
    if (groups.length) {
      const path = groups.map(group => getFieldValue(item, group.selector));
      const changedAt = path.findIndex(
        (key, index) => previousPath.length === 0 || String(key) !== String(previousPath[index])
      );

      if (changedAt >= 0)
        for (let level = changedAt; level < groups.length; level += 1) {
          const { column } = groups[level];

          rows.push({
            type: "group",
            level,
            text: `${column.caption}: ${getGroupKeyText(column, path[level], {
              ...locale,
              blankText: t("gridBlank"),
            })}`,
          });
        }

      previousPath = path;
    }

    rows.push({
      type: "data",
      level: groups.length,
      cells: columns.map(column => cellExport(column, item, locale, t)),
    });
  });

  return rows;
}

async function exportExcel({ fileName, columns, rows, widths }) {
  const [{ Workbook }, { saveAs }] = await Promise.all([
    import("exceljs"),
    import("file-saver-es"),
  ]);
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(sanitizeSheetName(fileName), {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  worksheet.columns = columns.map(column => ({
    header: column.caption,
    key: column.id,
    width: Math.min(Math.max(Math.round((widths[column.id] || 120) / 7), 8), 80),
  }));

  const header = worksheet.getRow(1);

  header.font = { bold: true };
  header.alignment = { vertical: "middle" };
  header.eachCell(cell => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF4F2F3" } };
    cell.border = { bottom: { style: "thin", color: { argb: "FFC4C4C6" } } };
  });

  rows.forEach(row => {
    if (row.type === "group") {
      const excelRow = worksheet.addRow([row.text]);

      excelRow.font = { bold: true };
      excelRow.outlineLevel = row.level;
      excelRow.getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3E3E4" },
      };
      worksheet.mergeCells(excelRow.number, 1, excelRow.number, Math.max(columns.length, 1));

      return;
    }

    const excelRow = worksheet.addRow(row.cells.map(cell => cell.value));

    excelRow.outlineLevel = row.level;
    row.cells.forEach((cell, index) => {
      const excelCell = excelRow.getCell(index + 1);

      if (cell.numFmt) excelCell.numFmt = cell.numFmt;
      if (columns[index].alignment === "right") excelCell.alignment = { horizontal: "right" };
    });
  });

  if (columns.length)
    worksheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } };

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${fileName}.xlsx`);
}

const latin1 = text => String(text ?? "").replace(/[^\x20-\xFF]/g, "?");

function fitText(doc, text, width) {
  const clean = latin1(text);

  if (doc.getTextWidth(clean) <= width) return clean;

  let low = 0;
  let high = clean.length;

  while (low < high) {
    const middle = Math.ceil((low + high) / 2);

    if (doc.getTextWidth(`${clean.slice(0, middle)}...`) <= width) low = middle;
    else high = middle - 1;
  }

  return `${clean.slice(0, low)}...`;
}

async function exportPdf({ fileName, columns, rows, widths, title }) {
  const { jsPDF: JsPDF } = await import("jspdf");
  const landscape = columns.length > 5;
  const doc = new JsPDF({
    orientation: landscape ? "landscape" : "portrait",
    unit: "pt",
    format: "a4",
  });
  const margin = 28;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usable = pageWidth - margin * 2;
  const rawWidths = columns.map(column => widths[column.id] || 120);
  const total = rawWidths.reduce((sum, width) => sum + width, 0) || 1;
  const columnWidths = rawWidths.map(width => (width / total) * usable);
  const rowHeight = 16;
  const padding = 4;
  let y = margin;

  doc.setFontSize(8);

  const drawHeader = () => {
    doc.setFont("helvetica", "bold");
    doc.setFillColor(244, 242, 243);
    doc.rect(margin, y, usable, rowHeight, "F");

    let x = margin;

    columns.forEach((column, index) => {
      doc.text(
        fitText(doc, column.caption, columnWidths[index] - padding * 2),
        x + padding,
        y + 11
      );
      x += columnWidths[index];
    });

    doc.setDrawColor(196, 196, 198);
    doc.line(margin, y + rowHeight, margin + usable, y + rowHeight);
    y += rowHeight;
    doc.setFont("helvetica", "normal");
  };

  if (title) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(latin1(title), margin, y + 10);
    doc.setFontSize(8);
    y += 22;
  }

  drawHeader();

  rows.forEach(row => {
    if (y + rowHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
      drawHeader();
    }

    if (row.type === "group") {
      doc.setFont("helvetica", "bold");
      doc.setFillColor(227, 227, 228);
      doc.rect(margin, y, usable, rowHeight, "F");
      doc.text(
        fitText(doc, row.text, usable - padding * 2 - row.level * 10),
        margin + padding + row.level * 10,
        y + 11
      );
      doc.setFont("helvetica", "normal");
    } else {
      let x = margin;

      row.cells.forEach((cell, index) => {
        const available = columnWidths[index] - padding * 2;
        const text = fitText(doc, cell.text, available);
        const right = columns[index].alignment === "right";

        doc.text(text, right ? x + columnWidths[index] - padding : x + padding, y + 11, {
          align: right ? "right" : "left",
        });
        x += columnWidths[index];
      });

      doc.setDrawColor(235, 235, 236);
      doc.line(margin, y + rowHeight, margin + usable, y + rowHeight);
    }

    y += rowHeight;
  });

  const pages = doc.getNumberOfPages();

  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.text(`${page} / ${pages}`, pageWidth - margin, pageHeight - 12, { align: "right" });
  }

  doc.save(`${fileName}.pdf`);
}

export async function exportGrid({
  format,
  fileName,
  title,
  columns,
  groups,
  loadAll,
  widths,
  locale,
  t,
}) {
  const data = await loadAll();
  const rows = buildRows(data, columns, groups, locale, t);
  const name = fileName || "export";

  if (format === "pdf") await exportPdf({ fileName: name, columns, rows, widths, title });
  else await exportExcel({ fileName: name, columns, rows, widths });
}
